import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { WifiOff } from 'lucide-react'

/**
 * Slim offline status strip under the app header (mounted in AppShell's
 * sticky status-strip wrapper beside ProductionTenantBanner — it appears
 * mid-session, so stickiness is the whole point).
 *
 * Deliberately NEUTRAL, not amber: DESIGN.md §2 reserves amber for
 * "waiting on you" decisions, and offline is nobody's decision — it's a
 * system state. No actions either: TanStack Query already pauses fetches
 * offline and refetches on reconnect, and writes fail loudly at their own
 * call sites (no offline queueing — the undo contract requires it).
 *
 * useSyncExternalStore over the RAW browser events (not TanStack's
 * onlineManager, which initializes online=true and would miss a
 * launched-while-offline PWA cold start): getSnapshot reads live, so the
 * launched-offline case and the render→subscribe gap are both covered.
 */
function subscribe(onChange: () => void) {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

export function OfflineBanner() {
  const { t } = useTranslation('common')
  const online = useSyncExternalStore(subscribe, () => navigator.onLine)

  if (online) return null

  return (
    <div role="status" className="border-b border-border bg-surface-2">
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium text-muted-foreground sm:px-6">
        <WifiOff className="size-3.5 shrink-0" aria-hidden />
        <span>{t('offline_banner')}</span>
      </div>
    </div>
  )
}
